// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as Core from './configuration.core';
import * as ModelLayout from './configuration.modelLayout';

export enum QuestionValidationStates {
    Valid,
    SoftInvalid,
    HardInvalid
}

export function isEntityModel(source: any): source is IEntity {
    // Method Implementation: simple
    return source.modelName === 'Entity';
}

export function isChildModel(source: any): source is IChild {
    // Method Implementation: simple
    return source.modelName === 'Child';
}

export function isGroupModel(source: any): source is IGroup {
    // Method Implementation: simple
    return source.modelName === 'Group';
}

export function isQuestionModel(source: any): source is IQuestion {
    // Method Implementation: simple
    return source.modelName === 'Question';
}

export function isStringQuestionModel(source: any): source is IStringQuestion {
    // Method Implementation: simple
    return source.modelName === 'StringQuestion';
}

export function isNumericQuestionModel(source: any): source is INumericQuestion {
    // Method Implementation: simple
    return source.modelName === 'NumericQuestion';
}

export function isBooleanQuestionModel(source: any): source is IBooleanQuestion {
    // Method Implementation: simple
    return source.modelName === 'BooleanQuestion';
}

export function isStringEnumerableQuestionModel(source: any): source is IStringEnumerableQuestion {
    // Method Implementation: simple
    return source.modelName === 'StringEnumerableQuestion';
}

export function isNumericEnumerableQuestionModel(source: any): source is INumericEnumerableQuestion {
    // Method Implementation: simple
    return source.modelName === 'NumericEnumerableQuestion';
}

export function isMultiStringQuestionModel(source: any): source is IMultiStringQuestion {
    // Method Implementation: simple
    return source.modelName === 'MultiStringQuestion';
}

export function isMultiNumericQuestionModel(source: any): source is IMultiNumericQuestion {
    // Method Implementation: simple
    return source.modelName === 'MultiNumericQuestion';
}

export function isAnswerModel(source: any): source is IAnswer {
    // Method Implementation: simple
    return source.modelName === 'Answer';
}

export function isStringAnswerModel(source: any): source is IStringAnswer {
    // Method Implementation: simple
    return source.modelName === 'StringAnswer';
}

export function isNumericAnswerModel(source: any): source is INumericAnswer {
    // Method Implementation: simple
    return source.modelName === 'NumericAnswer';
}

export interface EntityZodSchemaShape {
    entityId?: string;

    groups?: GroupZodSchemaShape[];

    children?: ChildZodSchemaShape[];

    validationMessages?: string[];
}

export interface ChildZodSchemaShape {
    name?: string;

    type?: string;

    source?: string;

    enabled?: boolean;

    visible?: boolean;

    entity?: EntityZodSchemaShape;
}

export interface GroupZodSchemaShape {
    name?: string;

    displayKey?: string;

    enabled?: boolean;

    visible?: boolean;

    questions?: QuestionZodSchemaShape[];
}

export interface QuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayKey?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: string[];

    valueSource?: string;
}

export interface StringQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayKey?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: string[];

    valueSource?: string;

    value?: string;
}

export interface NumericQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayKey?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: string[];

    valueSource?: string;

    value?: number;

    precision?: number;

    min?: number;

    max?: number;
}

export interface BooleanQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayKey?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: string[];

    valueSource?: string;

    value?: boolean;
}

export interface StringEnumerableQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayKey?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: string[];

    valueSource?: string;

    value?: string;

    answers?: StringAnswerZodSchemaShape[];
}

export interface NumericEnumerableQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayKey?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: string[];

    valueSource?: string;

    value?: number;

    answers?: NumericAnswerZodSchemaShape[];
}

export interface MultiStringQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayKey?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: string[];

    valueSource?: string;

    values?: string[];
}

export interface MultiNumericQuestionZodSchemaShape {
    questionType?: string;

    name?: string;

    source?: string;

    displayKey?: string;

    displayName?: string;

    helpText?: string;

    enabled?: boolean;

    visible?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: string[];

    valueSource?: string;

    values?: number[];
}

export interface AnswerZodSchemaShape {
    answerType?: string;

    name?: string;

    displayKey?: string;

    enabled?: boolean;

    visible?: boolean;

    assignmentState?: 'Assignable' | 'Conflicting' | 'Blocked';
}

export interface StringAnswerZodSchemaShape {
    answerType?: string;

    name?: string;

    displayKey?: string;

    enabled?: boolean;

    visible?: boolean;

    assignmentState?: 'Assignable' | 'Conflicting' | 'Blocked';

    value?: string;
}

export interface NumericAnswerZodSchemaShape {
    answerType?: string;

    name?: string;

    displayKey?: string;

    enabled?: boolean;

    visible?: boolean;

    assignmentState?: 'Assignable' | 'Conflicting' | 'Blocked';

    value?: number;
}

// Artifact Implementation: artifacts/zodSchemaType
export const EntityZodSchema: Zod.ZodSchema<EntityZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityId: Zod.string().optional(),
    groups: Zod.array(GroupZodSchema).optional(),
    children: Zod.array(ChildZodSchema).optional(),
    validationMessages: Zod.array(Zod.string()).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ChildZodSchema: Zod.ZodSchema<ChildZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    type: Zod.string().optional(),
    source: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    entity: EntityZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const GroupZodSchema: Zod.ZodSchema<GroupZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    questions: Zod.array(QuestionZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const QuestionZodSchema: Zod.ZodSchema<QuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(Zod.string()).optional(),
    valueSource: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringQuestionZodSchema: Zod.ZodSchema<StringQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(Zod.string()).optional(),
    valueSource: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericQuestionZodSchema: Zod.ZodSchema<NumericQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(Zod.string()).optional(),
    valueSource: Zod.string().optional(),
    value: Zod.number().optional(),
    precision: Zod.number().optional(),
    min: Zod.number().optional(),
    max: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const BooleanQuestionZodSchema: Zod.ZodSchema<BooleanQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(Zod.string()).optional(),
    valueSource: Zod.string().optional(),
    value: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringEnumerableQuestionZodSchema: Zod.ZodSchema<StringEnumerableQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(Zod.string()).optional(),
    valueSource: Zod.string().optional(),
    value: Zod.string().optional(),
    answers: Zod.array(StringAnswerZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericEnumerableQuestionZodSchema: Zod.ZodSchema<NumericEnumerableQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(Zod.string()).optional(),
    valueSource: Zod.string().optional(),
    value: Zod.number().optional(),
    answers: Zod.array(NumericAnswerZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MultiStringQuestionZodSchema: Zod.ZodSchema<MultiStringQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(Zod.string()).optional(),
    valueSource: Zod.string().optional(),
    values: Zod.array(Zod.string()).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MultiNumericQuestionZodSchema: Zod.ZodSchema<MultiNumericQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    questionType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(Zod.string()).optional(),
    valueSource: Zod.string().optional(),
    values: Zod.array(Zod.number()).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const AnswerZodSchema: Zod.ZodSchema<AnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    answerType: Zod.string(),
    name: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    assignmentState: Zod.enum([ 'Assignable', 'Conflicting', 'Blocked' ]).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringAnswerZodSchema: Zod.ZodSchema<StringAnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    answerType: Zod.string(),
    name: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    assignmentState: Zod.enum([ 'Assignable', 'Conflicting', 'Blocked' ]).optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericAnswerZodSchema: Zod.ZodSchema<NumericAnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    answerType: Zod.string(),
    name: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    assignmentState: Zod.enum([ 'Assignable', 'Conflicting', 'Blocked' ]).optional(),
    value: Zod.number().optional()
}).nonstrict());

export interface IEntity extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityId: string;

    readonly groups: Group[];

    readonly children: Child[];

    readonly validationMessages: string[];

    readonly isEntityIdDefined: boolean;

    readonly isGroupsDefined: boolean;

    readonly isChildrenDefined: boolean;

    readonly isValidationMessagesDefined: boolean;
}

export interface IChild extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly type: string;

    readonly source: string;

    readonly enabled: boolean;

    readonly visible: boolean;

    readonly entity: Entity;

    readonly isNameDefined: boolean;

    readonly isTypeDefined: boolean;

    readonly isSourceDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isEntityDefined: boolean;
}

export interface IGroup extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly displayKey: string;

    readonly enabled: boolean;

    readonly visible: boolean;

    readonly questions: AQuestion[];

    readonly isNameDefined: boolean;

    readonly isDisplayKeyDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isQuestionsDefined: boolean;
}

export interface IQuestion extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly source: string;

    readonly displayKey: string;

    readonly displayName: string;

    readonly helpText: string;

    readonly enabled: boolean;

    readonly visible: boolean;

    readonly validationState: QuestionValidationStates;

    readonly validationMessages: string[];

    readonly valueSource: string;

    readonly isNameDefined: boolean;

    readonly isSourceDefined: boolean;

    readonly isDisplayKeyDefined: boolean;

    readonly isDisplayNameDefined: boolean;

    readonly isHelpTextDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isValidationStateDefined: boolean;

    readonly isValidationMessagesDefined: boolean;

    readonly isValueSourceDefined: boolean;
}

export interface IStringQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface INumericQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly precision: number;

    readonly min: number;

    readonly max: number;

    readonly isValueDefined: boolean;

    readonly isPrecisionDefined: boolean;

    readonly isMinDefined: boolean;

    readonly isMaxDefined: boolean;
}

export interface IBooleanQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value: boolean;

    readonly isValueDefined: boolean;
}

export interface IStringEnumerableQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly answers: StringAnswer[];

    readonly isValueDefined: boolean;

    readonly isAnswersDefined: boolean;
}

export interface INumericEnumerableQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly answers: NumericAnswer[];

    readonly isValueDefined: boolean;

    readonly isAnswersDefined: boolean;
}

export interface IMultiStringQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly values: string[];

    readonly isValuesDefined: boolean;
}

export interface IMultiNumericQuestion extends IQuestion, Wellspring.IModel {
    readonly modelName: string;

    readonly values: number[];

    readonly isValuesDefined: boolean;
}

export interface IAnswer extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly displayKey: string;

    readonly enabled: boolean;

    readonly visible: boolean;

    readonly assignmentState: Core.OptionStates;

    readonly isNameDefined: boolean;

    readonly isDisplayKeyDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isAssignmentStateDefined: boolean;
}

export interface IStringAnswer extends IAnswer, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface INumericAnswer extends IAnswer, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export class Entity implements IEntity {
    public static readonly modelName: string = 'Entity';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityId', 'groups', 'children', 'validationMessages' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Entity.modelName;
    }

    private _entityId: string;

    private _groups: Group[];

    private _children: Child[];

    private _validationMessages: string[];

    public get entityId(): string {
        // Method Implementation: simple
        return this._entityId;
    }

    public get groups(): Group[] {
        // Method Implementation: simple
        return this._groups;
    }

    public get children(): Child[] {
        // Method Implementation: simple
        return this._children;
    }

    public get validationMessages(): string[] {
        // Method Implementation: simple
        return this._validationMessages;
    }

    public set entityId(value: string) {
        // Method Implementation: simple
        this._entityId = value;
        this._isEntityIdDefined = true;
    }

    public set groups(value: Group[]) {
        // Method Implementation: simple
        this._groups = value;
        this._isGroupsDefined = true;
    }

    public set children(value: Child[]) {
        // Method Implementation: simple
        this._children = value;
        this._isChildrenDefined = true;
    }

    public set validationMessages(value: string[]) {
        // Method Implementation: simple
        this._validationMessages = value;
        this._isValidationMessagesDefined = true;
    }

    private _isEntityIdDefined: boolean;

    private _isGroupsDefined: boolean;

    private _isChildrenDefined: boolean;

    private _isValidationMessagesDefined: boolean;

    public get isEntityIdDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityIdDefined;
    }

    public get isGroupsDefined(): boolean {
        // Method Implementation: simple
        return this._isGroupsDefined;
    }

    public get isChildrenDefined(): boolean {
        // Method Implementation: simple
        return this._isChildrenDefined;
    }

    public get isValidationMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isValidationMessagesDefined;
    }

    public constructor();
    public constructor(source: IEntity);
    public constructor(source: Partial<Pick<Entity, 'entityId' | 'groups' | 'children' | 'validationMessages'>>);
    public constructor(source?: IEntity | Partial<Pick<Entity, 'entityId' | 'groups' | 'children' | 'validationMessages'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityModel(source)) {
                if (source.isEntityIdDefined) {
                    this._entityId = source.entityId;
                    this._isEntityIdDefined = true;
                }
                else {
                    this._entityId = '';
                    this._isEntityIdDefined = false;
                }
                
                if (source.isGroupsDefined) {
                    this._groups = source.groups;
                    this._isGroupsDefined = true;
                }
                else {
                    this._groups = [];
                    this._isGroupsDefined = true;
                }
                
                if (source.isChildrenDefined) {
                    this._children = source.children;
                    this._isChildrenDefined = true;
                }
                else {
                    this._children = [];
                    this._isChildrenDefined = true;
                }
                
                if (source.isValidationMessagesDefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
            }
            else {
                if (source.entityId !== undefined) {
                    this._entityId = source.entityId;
                    this._isEntityIdDefined = true;
                }
                else {
                    this._entityId = '';
                    this._isEntityIdDefined = false;
                }
                
                if (source.groups !== undefined) {
                    this._groups = source.groups;
                    this._isGroupsDefined = true;
                }
                else {
                    this._groups = [];
                    this._isGroupsDefined = true;
                }
                
                if (source.children !== undefined) {
                    this._children = source.children;
                    this._isChildrenDefined = true;
                }
                else {
                    this._children = [];
                    this._isChildrenDefined = true;
                }
                
                if (source.validationMessages !== undefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
            }
        }
        else {
            this._entityId = '';
            this._isEntityIdDefined = false;
        
            this._groups = [];
            this._isGroupsDefined = true;
        
            this._children = [];
            this._isChildrenDefined = true;
        
            this._validationMessages = [];
            this._isValidationMessagesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Entity.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityIdDefined && this.entityId !== undefined)
            definedProperties.push('entityId');
        if (this.isGroupsDefined && this.groups !== undefined)
            definedProperties.push('groups');
        if (this.isChildrenDefined && this.children !== undefined)
            definedProperties.push('children');
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined)
            definedProperties.push('validationMessages');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityId':
                return this.entityId;
            case 'groups':
                return this.groups;
            case 'children':
                return this.children;
            case 'validationMessages':
                return this.validationMessages;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityIdDefined && this.entityId !== undefined) {
            if (this.entityId !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityId', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityId);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityId', 'null'));
            }
        }
        
        if (this.isGroupsDefined && this.groups !== undefined) {
            if (this.groups !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groups', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.groups.length));
                for (let i = 0; i < this.groups.length; i++) {
                    const item = this.groups[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groups', 'null'));
            }
        }
        
        if (this.isChildrenDefined && this.children !== undefined) {
            if (this.children !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('children', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.children.length));
                for (let i = 0; i < this.children.length; i++) {
                    const item = this.children[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('children', 'null'));
            }
        }
        
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            if (this.validationMessages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validationMessages.length));
                for (let i = 0; i < this.validationMessages.length; i++) {
                    const item = this.validationMessages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityId':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityId = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityId' cannot be null`);
                    this.entityId = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityId'`);
                }
        
                return true;
            case 'groups':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.groups = [];
                
                    let item: Group = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Group.modelName) {
                                item = new Group();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Group.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'groups' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'groups'`);
                        }
                
                        this.groups.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'groups' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'groups'`);
                }
        
                return true;
            case 'children':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.children = [];
                
                    let item: Child = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Child.modelName) {
                                item = new Child();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Child.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'children' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'children'`);
                        }
                
                        this.children.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'children' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'children'`);
                }
        
                return true;
            case 'validationMessages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validationMessages = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'validationMessages' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validationMessages'`);
                        }
                
                        this.validationMessages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validationMessages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validationMessages'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityId
        if (this.isEntityIdDefined && this.entityId !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityId === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityId);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // groups
        if (this.isGroupsDefined && this.groups !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.groups === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.groups.length);
            
                for (const item of this.groups) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // children
        if (this.isChildrenDefined && this.children !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.children === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.children.length);
            
                for (const item of this.children) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validationMessages
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validationMessages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validationMessages.length);
            
                for (const item of this.validationMessages) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityId
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityId = <any>null;
                    break;
                case 1:
                    this.entityId = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // groups
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Group;
            
                    this.groups = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Group();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.groups.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // children
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Child;
            
                    this.children = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Child();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.children.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // validationMessages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.validationMessages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validationMessages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Entity {
        // Method Implementation: simple
        const schema = EntityZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityZodSchemaShape): Entity {
        // Method Implementation: parseZodSchema
        return new Entity({
            entityId: schema.entityId,
            groups: schema.groups?.map(i => Group.parseZodSchema(i)),
            children: schema.children?.map(i => Child.parseZodSchema(i)),
            validationMessages: schema.validationMessages?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityId: this.isEntityIdDefined ? ((this.entityId === null ? undefined : this.entityId)) : undefined,
            groups: this.isGroupsDefined ? (this.groups?.map(item => item)) : undefined,
            children: this.isChildrenDefined ? (this.children?.map(item => item)) : undefined,
            validationMessages: this.isValidationMessagesDefined ? (this.validationMessages?.map(item => (item === null ? undefined : item))) : undefined,
        };
    }

    public clone(): Entity {
        // Method Implementation: clone
        return new Entity({
            entityId: this.isEntityIdDefined ? this.entityId : undefined,
            groups: this.isGroupsDefined ? this.groups?.map(i => i.clone()) : undefined,
            children: this.isChildrenDefined ? this.children?.map(i => i.clone()) : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i) : undefined
        });
    }
}

export class Child implements IChild {
    public static readonly modelName: string = 'Child';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'type', 'source', 'enabled', 'visible', 'entity' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Child.modelName;
    }

    private _name: string;

    private _type: string;

    private _source: string;

    private _enabled: boolean;

    private _visible: boolean;

    private _entity: Entity;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get type(): string {
        // Method Implementation: simple
        return this._type;
    }

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get enabled(): boolean {
        // Method Implementation: simple
        return this._enabled;
    }

    public get visible(): boolean {
        // Method Implementation: simple
        return this._visible;
    }

    public get entity(): Entity {
        // Method Implementation: simple
        return this._entity;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set type(value: string) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set enabled(value: boolean) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set visible(value: boolean) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set entity(value: Entity) {
        // Method Implementation: simple
        this._entity = value;
        this._isEntityDefined = true;
    }

    private _isNameDefined: boolean;

    private _isTypeDefined: boolean;

    private _isSourceDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isEntityDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isEntityDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityDefined;
    }

    public constructor();
    public constructor(source: IChild);
    public constructor(source: Partial<Pick<Child, 'name' | 'type' | 'source' | 'enabled' | 'visible' | 'entity'>>);
    public constructor(source?: IChild | Partial<Pick<Child, 'name' | 'type' | 'source' | 'enabled' | 'visible' | 'entity'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isChildModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
                
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.isEntityDefined) {
                    this._entity = source.entity;
                    this._isEntityDefined = true;
                }
                else {
                    this._entity = new Entity();
                    this._isEntityDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
                
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.entity !== undefined) {
                    this._entity = source.entity;
                    this._isEntityDefined = true;
                }
                else {
                    this._entity = new Entity();
                    this._isEntityDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._type = '';
            this._isTypeDefined = false;
        
            this._source = '';
            this._isSourceDefined = false;
        
            this._enabled = false;
            this._isEnabledDefined = false;
        
            this._visible = false;
            this._isVisibleDefined = false;
        
            this._entity = new Entity();
            this._isEntityDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Child.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isEntityDefined && this.entity !== undefined)
            definedProperties.push('entity');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'type':
                return this.type;
            case 'source':
                return this.source;
            case 'enabled':
                return this.enabled;
            case 'visible':
                return this.visible;
            case 'entity':
                return this.entity;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
        
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isEntityDefined && this.entity !== undefined) {
            if (this.entity !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entity', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.entity.getModelName(), this.entity.getDefinedPropertyNames().length));
                this.entity.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entity', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.type = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'type' cannot be null`);
                    this.type = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.enabled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'enabled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.visible = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'visible' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'entity':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === Entity.modelName) {
                        this.entity = new Entity();
                        this.entity.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Entity.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'entity' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entity'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.type === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.type);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.enabled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.visible);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // entity
        if (this.isEntityDefined && this.entity !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.entity === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.entity.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.type = <any>null;
                    break;
                case 1:
                    this.type = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.enabled = reader.readBooleanValue();
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.visible = reader.readBooleanValue();
        }
        
        // entity
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.entity = new Entity();
                    this.entity.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Child {
        // Method Implementation: simple
        const schema = ChildZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ChildZodSchemaShape): Child {
        // Method Implementation: parseZodSchema
        return new Child({
            name: schema.name,
            type: schema.type,
            source: schema.source,
            enabled: schema.enabled,
            visible: schema.visible,
            entity: schema.entity && Entity.parseZodSchema(schema.entity)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            type: this.isTypeDefined ? ((this.type === null ? undefined : this.type)) : undefined,
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            enabled: this.isEnabledDefined ? (this.enabled) : undefined,
            visible: this.isVisibleDefined ? (this.visible) : undefined,
            entity: this.isEntityDefined ? (this.entity) : undefined,
        };
    }

    public clone(): Child {
        // Method Implementation: clone
        return new Child({
            name: this.isNameDefined ? this.name : undefined,
            type: this.isTypeDefined ? this.type : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            entity: this.isEntityDefined ? this.entity.clone() : undefined
        });
    }
}

export class Group implements IGroup {
    public static readonly modelName: string = 'Group';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'displayKey', 'enabled', 'visible', 'questions' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Group.modelName;
    }

    private _name: string;

    private _displayKey: string;

    private _enabled: boolean;

    private _visible: boolean;

    private _questions: AQuestion[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get displayKey(): string {
        // Method Implementation: simple
        return this._displayKey;
    }

    public get enabled(): boolean {
        // Method Implementation: simple
        return this._enabled;
    }

    public get visible(): boolean {
        // Method Implementation: simple
        return this._visible;
    }

    public get questions(): AQuestion[] {
        // Method Implementation: simple
        return this._questions;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set displayKey(value: string) {
        // Method Implementation: simple
        this._displayKey = value;
        this._isDisplayKeyDefined = true;
    }

    public set enabled(value: boolean) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set visible(value: boolean) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set questions(value: AQuestion[]) {
        // Method Implementation: simple
        this._questions = value;
        this._isQuestionsDefined = true;
    }

    private _isNameDefined: boolean;

    private _isDisplayKeyDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isQuestionsDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isDisplayKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayKeyDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isQuestionsDefined(): boolean {
        // Method Implementation: simple
        return this._isQuestionsDefined;
    }

    public constructor();
    public constructor(source: IGroup);
    public constructor(source: Partial<Pick<Group, 'name' | 'displayKey' | 'enabled' | 'visible' | 'questions'>>);
    public constructor(source?: IGroup | Partial<Pick<Group, 'name' | 'displayKey' | 'enabled' | 'visible' | 'questions'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isGroupModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isDisplayKeyDefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.isQuestionsDefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.displayKey !== undefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.questions !== undefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._displayKey = '';
            this._isDisplayKeyDefined = false;
        
            this._enabled = false;
            this._isEnabledDefined = false;
        
            this._visible = false;
            this._isVisibleDefined = false;
        
            this._questions = [];
            this._isQuestionsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Group.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isDisplayKeyDefined && this.displayKey !== undefined)
            definedProperties.push('displayKey');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isQuestionsDefined && this.questions !== undefined)
            definedProperties.push('questions');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'displayKey':
                return this.displayKey;
            case 'enabled':
                return this.enabled;
            case 'visible':
                return this.visible;
            case 'questions':
                return this.questions;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            if (this.displayKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isQuestionsDefined && this.questions !== undefined) {
            if (this.questions !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.questions.length));
                for (let i = 0; i < this.questions.length; i++) {
                    const item = this.questions[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'displayKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayKey' cannot be null`);
                    this.displayKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayKey'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.enabled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'enabled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.visible = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'visible' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'questions':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.questions = [];
                
                    let item: AQuestion = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'questions' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'questions'`);
                        }
                
                        this.questions.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'questions' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'questions'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayKey
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.enabled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.visible);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // questions
        if (this.isQuestionsDefined && this.questions !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.questions === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.questions.length);
            
                for (const item of this.questions) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof StringQuestion)
                            writer.writeShortValue(0);
                        else if (item instanceof NumericQuestion)
                            writer.writeShortValue(1);
                        else if (item instanceof BooleanQuestion)
                            writer.writeShortValue(2);
                        else if (item instanceof StringEnumerableQuestion)
                            writer.writeShortValue(3);
                        else if (item instanceof NumericEnumerableQuestion)
                            writer.writeShortValue(4);
                        else if (item instanceof MultiStringQuestion)
                            writer.writeShortValue(5);
                        else if (item instanceof MultiNumericQuestion)
                            writer.writeShortValue(6);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayKey = <any>null;
                    break;
                case 1:
                    this.displayKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.enabled = reader.readBooleanValue();
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.visible = reader.readBooleanValue();
        }
        
        // questions
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: AQuestion;
            
                    this.questions = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new StringQuestion();
                                        break;
                                    case 1:
                                        item = new NumericQuestion();
                                        break;
                                    case 2:
                                        item = new BooleanQuestion();
                                        break;
                                    case 3:
                                        item = new StringEnumerableQuestion();
                                        break;
                                    case 4:
                                        item = new NumericEnumerableQuestion();
                                        break;
                                    case 5:
                                        item = new MultiStringQuestion();
                                        break;
                                    case 6:
                                        item = new MultiNumericQuestion();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.questions.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Group {
        // Method Implementation: simple
        const schema = GroupZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: GroupZodSchemaShape): Group {
        // Method Implementation: parseZodSchema
        return new Group({
            name: schema.name,
            displayKey: schema.displayKey,
            enabled: schema.enabled,
            visible: schema.visible,
            questions: schema.questions?.map(i => AQuestion.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            displayKey: this.isDisplayKeyDefined ? ((this.displayKey === null ? undefined : this.displayKey)) : undefined,
            enabled: this.isEnabledDefined ? (this.enabled) : undefined,
            visible: this.isVisibleDefined ? (this.visible) : undefined,
            questions: this.isQuestionsDefined ? (this.questions?.map(item => item)) : undefined,
        };
    }

    public clone(): Group {
        // Method Implementation: clone
        return new Group({
            name: this.isNameDefined ? this.name : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            questions: this.isQuestionsDefined ? this.questions?.map(i => i.clone()) : undefined
        });
    }
}

export abstract class AQuestion implements IQuestion {
    public static readonly modelName: string = 'Question';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'source', 'displayKey', 'displayName', 'helpText', 'enabled', 'visible', 'validationState', 'validationMessages', 'valueSource' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AQuestion.modelName;
    }

    private _name: string;

    private _source: string;

    private _displayKey: string;

    private _displayName: string;

    private _helpText: string;

    private _enabled: boolean;

    private _visible: boolean;

    private _validationState: QuestionValidationStates;

    private _validationMessages: string[];

    private _valueSource: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get displayKey(): string {
        // Method Implementation: simple
        return this._displayKey;
    }

    public get displayName(): string {
        // Method Implementation: simple
        return this._displayName;
    }

    public get helpText(): string {
        // Method Implementation: simple
        return this._helpText;
    }

    public get enabled(): boolean {
        // Method Implementation: simple
        return this._enabled;
    }

    public get visible(): boolean {
        // Method Implementation: simple
        return this._visible;
    }

    public get validationState(): QuestionValidationStates {
        // Method Implementation: simple
        return this._validationState;
    }

    public get validationMessages(): string[] {
        // Method Implementation: simple
        return this._validationMessages;
    }

    public get valueSource(): string {
        // Method Implementation: simple
        return this._valueSource;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set displayKey(value: string) {
        // Method Implementation: simple
        this._displayKey = value;
        this._isDisplayKeyDefined = true;
    }

    public set displayName(value: string) {
        // Method Implementation: simple
        this._displayName = value;
        this._isDisplayNameDefined = true;
    }

    public set helpText(value: string) {
        // Method Implementation: simple
        this._helpText = value;
        this._isHelpTextDefined = true;
    }

    public set enabled(value: boolean) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set visible(value: boolean) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set validationState(value: QuestionValidationStates) {
        // Method Implementation: simple
        this._validationState = value;
        this._isValidationStateDefined = true;
    }

    public set validationMessages(value: string[]) {
        // Method Implementation: simple
        this._validationMessages = value;
        this._isValidationMessagesDefined = true;
    }

    public set valueSource(value: string) {
        // Method Implementation: simple
        this._valueSource = value;
        this._isValueSourceDefined = true;
    }

    private _isNameDefined: boolean;

    private _isSourceDefined: boolean;

    private _isDisplayKeyDefined: boolean;

    private _isDisplayNameDefined: boolean;

    private _isHelpTextDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isValidationStateDefined: boolean;

    private _isValidationMessagesDefined: boolean;

    private _isValueSourceDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isDisplayKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayKeyDefined;
    }

    public get isDisplayNameDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayNameDefined;
    }

    public get isHelpTextDefined(): boolean {
        // Method Implementation: simple
        return this._isHelpTextDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isValidationStateDefined(): boolean {
        // Method Implementation: simple
        return this._isValidationStateDefined;
    }

    public get isValidationMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isValidationMessagesDefined;
    }

    public get isValueSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isValueSourceDefined;
    }

    public constructor();
    public constructor(source: IQuestion);
    public constructor(source: Partial<Pick<AQuestion, 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>);
    public constructor(source?: IQuestion | Partial<Pick<AQuestion, 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isQuestionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isDisplayKeyDefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
                
                if (source.isDisplayNameDefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.isHelpTextDefined) {
                    this._helpText = source.helpText;
                    this._isHelpTextDefined = true;
                }
                else {
                    this._helpText = '';
                    this._isHelpTextDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.isValidationStateDefined) {
                    this._validationState = source.validationState;
                    this._isValidationStateDefined = true;
                }
                else {
                    this._validationState = QuestionValidationStates.Valid;
                    this._isValidationStateDefined = false;
                }
                
                if (source.isValidationMessagesDefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
                
                if (source.isValueSourceDefined) {
                    this._valueSource = source.valueSource;
                    this._isValueSourceDefined = true;
                }
                else {
                    this._valueSource = '';
                    this._isValueSourceDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.displayKey !== undefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
                
                if (source.displayName !== undefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.helpText !== undefined) {
                    this._helpText = source.helpText;
                    this._isHelpTextDefined = true;
                }
                else {
                    this._helpText = '';
                    this._isHelpTextDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.validationState !== undefined) {
                    this._validationState = source.validationState;
                    this._isValidationStateDefined = true;
                }
                else {
                    this._validationState = QuestionValidationStates.Valid;
                    this._isValidationStateDefined = false;
                }
                
                if (source.validationMessages !== undefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
                
                if (source.valueSource !== undefined) {
                    this._valueSource = source.valueSource;
                    this._isValueSourceDefined = true;
                }
                else {
                    this._valueSource = '';
                    this._isValueSourceDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._source = '';
            this._isSourceDefined = false;
        
            this._displayKey = '';
            this._isDisplayKeyDefined = false;
        
            this._displayName = '';
            this._isDisplayNameDefined = false;
        
            this._helpText = '';
            this._isHelpTextDefined = false;
        
            this._enabled = false;
            this._isEnabledDefined = false;
        
            this._visible = false;
            this._isVisibleDefined = false;
        
            this._validationState = QuestionValidationStates.Valid;
            this._isValidationStateDefined = false;
        
            this._validationMessages = [];
            this._isValidationMessagesDefined = true;
        
            this._valueSource = '';
            this._isValueSourceDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AQuestion.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isDisplayKeyDefined && this.displayKey !== undefined)
            definedProperties.push('displayKey');
        if (this.isDisplayNameDefined && this.displayName !== undefined)
            definedProperties.push('displayName');
        if (this.isHelpTextDefined && this.helpText !== undefined)
            definedProperties.push('helpText');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isValidationStateDefined && this.validationState !== undefined)
            definedProperties.push('validationState');
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined)
            definedProperties.push('validationMessages');
        if (this.isValueSourceDefined && this.valueSource !== undefined)
            definedProperties.push('valueSource');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'source':
                return this.source;
            case 'displayKey':
                return this.displayKey;
            case 'displayName':
                return this.displayName;
            case 'helpText':
                return this.helpText;
            case 'enabled':
                return this.enabled;
            case 'visible':
                return this.visible;
            case 'validationState':
                return this.validationState;
            case 'validationMessages':
                return this.validationMessages;
            case 'valueSource':
                return this.valueSource;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            if (this.displayKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'null'));
            }
        }
        
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            if (this.displayName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'null'));
            }
        }
        
        if (this.isHelpTextDefined && this.helpText !== undefined) {
            if (this.helpText !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('helpText', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.helpText);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('helpText', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isValidationStateDefined && this.validationState !== undefined) {
            if (this.validationState !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationState', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.validationState);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationState', 'null'));
            }
        }
        
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            if (this.validationMessages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validationMessages.length));
                for (let i = 0; i < this.validationMessages.length; i++) {
                    const item = this.validationMessages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'null'));
            }
        }
        
        if (this.isValueSourceDefined && this.valueSource !== undefined) {
            if (this.valueSource !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('valueSource', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.valueSource);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('valueSource', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'displayKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayKey' cannot be null`);
                    this.displayKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayKey'`);
                }
        
                return true;
            case 'displayName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayName' cannot be null`);
                    this.displayName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayName'`);
                }
        
                return true;
            case 'helpText':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.helpText = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'helpText' cannot be null`);
                    this.helpText = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'helpText'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.enabled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'enabled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.visible = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'visible' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'validationState':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.validationState = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'validationState' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validationState'`);
                }
        
                return true;
            case 'validationMessages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validationMessages = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'validationMessages' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validationMessages'`);
                        }
                
                        this.validationMessages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validationMessages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validationMessages'`);
                }
        
                return true;
            case 'valueSource':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.valueSource = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'valueSource' cannot be null`);
                    this.valueSource = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'valueSource'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayKey
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayName
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // helpText
        if (this.isHelpTextDefined && this.helpText !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.helpText === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.helpText);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.enabled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.visible);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validationState
        if (this.isValidationStateDefined && this.validationState !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.validationState);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validationMessages
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validationMessages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validationMessages.length);
            
                for (const item of this.validationMessages) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // valueSource
        if (this.isValueSourceDefined && this.valueSource !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.valueSource === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.valueSource);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayKey = <any>null;
                    break;
                case 1:
                    this.displayKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayName = <any>null;
                    break;
                case 1:
                    this.displayName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // helpText
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.helpText = <any>null;
                    break;
                case 1:
                    this.helpText = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.enabled = reader.readBooleanValue();
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.visible = reader.readBooleanValue();
        }
        
        // validationState
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.validationState = reader.readEnumValue();
        }
        
        // validationMessages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.validationMessages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validationMessages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // valueSource
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.valueSource = <any>null;
                    break;
                case 1:
                    this.valueSource = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AQuestion {
        // Method Implementation: simple
        const schema = QuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: QuestionZodSchemaShape): AQuestion {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.questionType)
        {
            case 'stringQuestion':
            case 'string':
                return StringQuestion.parse(schema);
            case 'numericQuestion':
            case 'numeric':
                return NumericQuestion.parse(schema);
            case 'booleanQuestion':
            case 'boolean':
                return BooleanQuestion.parse(schema);
            case 'stringEnumerableQuestion':
            case 'stringEnumerable':
                return StringEnumerableQuestion.parse(schema);
            case 'numericEnumerableQuestion':
            case 'numericEnumerable':
                return NumericEnumerableQuestion.parse(schema);
            case 'multiStringQuestion':
            case 'multiString':
                return MultiStringQuestion.parse(schema);
            case 'multiNumericQuestion':
            case 'multiNumeric':
                return MultiNumericQuestion.parse(schema);
            default:
                throw new Error(`questionType of ${ schema.questionType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            displayKey: this.isDisplayKeyDefined ? ((this.displayKey === null ? undefined : this.displayKey)) : undefined,
            displayName: this.isDisplayNameDefined ? ((this.displayName === null ? undefined : this.displayName)) : undefined,
            helpText: this.isHelpTextDefined ? ((this.helpText === null ? undefined : this.helpText)) : undefined,
            enabled: this.isEnabledDefined ? (this.enabled) : undefined,
            visible: this.isVisibleDefined ? (this.visible) : undefined,
            validationState: this.isValidationStateDefined ? (QuestionValidationStates[this.validationState]) : undefined,
            validationMessages: this.isValidationMessagesDefined ? (this.validationMessages?.map(item => (item === null ? undefined : item))) : undefined,
            valueSource: this.isValueSourceDefined ? ((this.valueSource === null ? undefined : this.valueSource)) : undefined,
        };
    }

    public abstract clone(): AQuestion;
}

export class StringQuestion extends AQuestion implements IStringQuestion {
    public static readonly modelName: string = 'StringQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringQuestion.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringQuestion);
    public constructor(source: Partial<Pick<StringQuestion, 'value' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>);
    public constructor(source?: IStringQuestion | Partial<Pick<StringQuestion, 'value' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringQuestion {
        // Method Implementation: simple
        const schema = StringQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringQuestionZodSchemaShape): StringQuestion {
        // Method Implementation: parseZodSchema
        return new StringQuestion({
            name: schema.name,
            source: schema.source,
            displayKey: schema.displayKey,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => i),
            valueSource: schema.valueSource,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringQuestion {
        // Method Implementation: clone
        return new StringQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericQuestion extends AQuestion implements INumericQuestion {
    public static readonly modelName: string = 'NumericQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'precision', 'min', 'max' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericQuestion.modelName;
    }

    private _value: number;

    private _precision: number;

    private _min: number;

    private _max: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public get min(): number {
        // Method Implementation: simple
        return this._min;
    }

    public get max(): number {
        // Method Implementation: simple
        return this._max;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    public set min(value: number) {
        // Method Implementation: simple
        this._min = value;
        this._isMinDefined = true;
    }

    public set max(value: number) {
        // Method Implementation: simple
        this._max = value;
        this._isMaxDefined = true;
    }

    private _isValueDefined: boolean;

    private _isPrecisionDefined: boolean;

    private _isMinDefined: boolean;

    private _isMaxDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public get isMinDefined(): boolean {
        // Method Implementation: simple
        return this._isMinDefined;
    }

    public get isMaxDefined(): boolean {
        // Method Implementation: simple
        return this._isMaxDefined;
    }

    public constructor();
    public constructor(source: INumericQuestion);
    public constructor(source: Partial<Pick<NumericQuestion, 'value' | 'precision' | 'min' | 'max' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>);
    public constructor(source?: INumericQuestion | Partial<Pick<NumericQuestion, 'value' | 'precision' | 'min' | 'max' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.isMinDefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.isMaxDefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.min !== undefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.max !== undefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        
            this._min = 0;
            this._isMinDefined = false;
        
            this._max = 0;
            this._isMaxDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        if (this.isMinDefined && this.min !== undefined)
            definedProperties.push('min');
        if (this.isMaxDefined && this.max !== undefined)
            definedProperties.push('max');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'precision':
                return this.precision;
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
        
        if (this.isMinDefined && this.min !== undefined) {
            if (this.min !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.min);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'null'));
            }
        }
        
        if (this.isMaxDefined && this.max !== undefined) {
            if (this.max !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.max);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            case 'min':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.min = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.min = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'min' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'min'`);
                }
        
                return true;
            case 'max':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.max = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.max = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'max' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'max'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // min
        if (this.isMinDefined && this.min !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.min);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // max
        if (this.isMaxDefined && this.max !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.max);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
        
        // min
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.min = reader.readDoubleValue();
        }
        
        // max
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.max = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericQuestion {
        // Method Implementation: simple
        const schema = NumericQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericQuestionZodSchemaShape): NumericQuestion {
        // Method Implementation: parseZodSchema
        return new NumericQuestion({
            name: schema.name,
            source: schema.source,
            displayKey: schema.displayKey,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => i),
            valueSource: schema.valueSource,
            value: schema.value,
            precision: schema.precision,
            min: schema.min,
            max: schema.max
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
            min: this.isMinDefined ? (this.min) : undefined,
            max: this.isMaxDefined ? (this.max) : undefined,
        };
    }

    public clone(): NumericQuestion {
        // Method Implementation: clone
        return new NumericQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            value: this.isValueDefined ? this.value : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined,
            min: this.isMinDefined ? this.min : undefined,
            max: this.isMaxDefined ? this.max : undefined
        });
    }
}

export class BooleanQuestion extends AQuestion implements IBooleanQuestion {
    public static readonly modelName: string = 'BooleanQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return BooleanQuestion.modelName;
    }

    private _value: boolean;

    public get value(): boolean {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: boolean) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IBooleanQuestion);
    public constructor(source: Partial<Pick<BooleanQuestion, 'value' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>);
    public constructor(source?: IBooleanQuestion | Partial<Pick<BooleanQuestion, 'value' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isBooleanQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = false;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...BooleanQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.value = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.value = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): BooleanQuestion {
        // Method Implementation: simple
        const schema = BooleanQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: BooleanQuestionZodSchemaShape): BooleanQuestion {
        // Method Implementation: parseZodSchema
        return new BooleanQuestion({
            name: schema.name,
            source: schema.source,
            displayKey: schema.displayKey,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => i),
            valueSource: schema.valueSource,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'boolean',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): BooleanQuestion {
        // Method Implementation: clone
        return new BooleanQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class StringEnumerableQuestion extends AQuestion implements IStringEnumerableQuestion {
    public static readonly modelName: string = 'StringEnumerableQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'answers' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringEnumerableQuestion.modelName;
    }

    private _value: string;

    private _answers: StringAnswer[];

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public get answers(): StringAnswer[] {
        // Method Implementation: simple
        return this._answers;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set answers(value: StringAnswer[]) {
        // Method Implementation: simple
        this._answers = value;
        this._isAnswersDefined = true;
    }

    private _isValueDefined: boolean;

    private _isAnswersDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isAnswersDefined(): boolean {
        // Method Implementation: simple
        return this._isAnswersDefined;
    }

    public constructor();
    public constructor(source: IStringEnumerableQuestion);
    public constructor(source: Partial<Pick<StringEnumerableQuestion, 'value' | 'answers' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>);
    public constructor(source?: IStringEnumerableQuestion | Partial<Pick<StringEnumerableQuestion, 'value' | 'answers' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringEnumerableQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.isAnswersDefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.answers !== undefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        
            this._answers = [];
            this._isAnswersDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringEnumerableQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isAnswersDefined && this.answers !== undefined)
            definedProperties.push('answers');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'answers':
                return this.answers;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isAnswersDefined && this.answers !== undefined) {
            if (this.answers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.answers.length));
                for (let i = 0; i < this.answers.length; i++) {
                    const item = this.answers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'answers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.answers = [];
                
                    let item: StringAnswer = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === StringAnswer.modelName) {
                                item = new StringAnswer();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${StringAnswer.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'answers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'answers'`);
                        }
                
                        this.answers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'answers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'answers'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // answers
        if (this.isAnswersDefined && this.answers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.answers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.answers.length);
            
                for (const item of this.answers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // answers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: StringAnswer;
            
                    this.answers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new StringAnswer();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.answers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringEnumerableQuestion {
        // Method Implementation: simple
        const schema = StringEnumerableQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringEnumerableQuestionZodSchemaShape): StringEnumerableQuestion {
        // Method Implementation: parseZodSchema
        return new StringEnumerableQuestion({
            name: schema.name,
            source: schema.source,
            displayKey: schema.displayKey,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => i),
            valueSource: schema.valueSource,
            value: schema.value,
            answers: schema.answers?.map(i => StringAnswer.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'stringEnumerable',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
            answers: this.isAnswersDefined ? (this.answers?.map(item => item)) : undefined,
        };
    }

    public clone(): StringEnumerableQuestion {
        // Method Implementation: clone
        return new StringEnumerableQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            value: this.isValueDefined ? this.value : undefined,
            answers: this.isAnswersDefined ? this.answers?.map(i => i.clone()) : undefined
        });
    }
}

export class NumericEnumerableQuestion extends AQuestion implements INumericEnumerableQuestion {
    public static readonly modelName: string = 'NumericEnumerableQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'answers' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericEnumerableQuestion.modelName;
    }

    private _value: number;

    private _answers: NumericAnswer[];

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public get answers(): NumericAnswer[] {
        // Method Implementation: simple
        return this._answers;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set answers(value: NumericAnswer[]) {
        // Method Implementation: simple
        this._answers = value;
        this._isAnswersDefined = true;
    }

    private _isValueDefined: boolean;

    private _isAnswersDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isAnswersDefined(): boolean {
        // Method Implementation: simple
        return this._isAnswersDefined;
    }

    public constructor();
    public constructor(source: INumericEnumerableQuestion);
    public constructor(source: Partial<Pick<NumericEnumerableQuestion, 'value' | 'answers' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>);
    public constructor(source?: INumericEnumerableQuestion | Partial<Pick<NumericEnumerableQuestion, 'value' | 'answers' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericEnumerableQuestionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.isAnswersDefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.answers !== undefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        
            this._answers = [];
            this._isAnswersDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericEnumerableQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isAnswersDefined && this.answers !== undefined)
            definedProperties.push('answers');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'answers':
                return this.answers;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isAnswersDefined && this.answers !== undefined) {
            if (this.answers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.answers.length));
                for (let i = 0; i < this.answers.length; i++) {
                    const item = this.answers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'answers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.answers = [];
                
                    let item: NumericAnswer = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === NumericAnswer.modelName) {
                                item = new NumericAnswer();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${NumericAnswer.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'answers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'answers'`);
                        }
                
                        this.answers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'answers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'answers'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // answers
        if (this.isAnswersDefined && this.answers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.answers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.answers.length);
            
                for (const item of this.answers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
        
        // answers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: NumericAnswer;
            
                    this.answers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new NumericAnswer();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.answers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): NumericEnumerableQuestion {
        // Method Implementation: simple
        const schema = NumericEnumerableQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericEnumerableQuestionZodSchemaShape): NumericEnumerableQuestion {
        // Method Implementation: parseZodSchema
        return new NumericEnumerableQuestion({
            name: schema.name,
            source: schema.source,
            displayKey: schema.displayKey,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => i),
            valueSource: schema.valueSource,
            value: schema.value,
            answers: schema.answers?.map(i => NumericAnswer.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'numericEnumerable',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
            answers: this.isAnswersDefined ? (this.answers?.map(item => item)) : undefined,
        };
    }

    public clone(): NumericEnumerableQuestion {
        // Method Implementation: clone
        return new NumericEnumerableQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            value: this.isValueDefined ? this.value : undefined,
            answers: this.isAnswersDefined ? this.answers?.map(i => i.clone()) : undefined
        });
    }
}

export class MultiStringQuestion extends AQuestion implements IMultiStringQuestion {
    public static readonly modelName: string = 'MultiStringQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'values' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MultiStringQuestion.modelName;
    }

    private _values: string[];

    public get values(): string[] {
        // Method Implementation: simple
        return this._values;
    }

    public set values(value: string[]) {
        // Method Implementation: simple
        this._values = value;
        this._isValuesDefined = true;
    }

    private _isValuesDefined: boolean;

    public get isValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isValuesDefined;
    }

    public constructor();
    public constructor(source: IMultiStringQuestion);
    public constructor(source: Partial<Pick<MultiStringQuestion, 'values' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>);
    public constructor(source?: IMultiStringQuestion | Partial<Pick<MultiStringQuestion, 'values' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isMultiStringQuestionModel(source)) {
                if (source.isValuesDefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
            else {
                if (source.values !== undefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._values = [];
            this._isValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...MultiStringQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValuesDefined && this.values !== undefined)
            definedProperties.push('values');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'values':
                return this.values;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValuesDefined && this.values !== undefined) {
            if (this.values !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.values.length));
                for (let i = 0; i < this.values.length; i++) {
                    const item = this.values[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'values':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.values = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'values' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'values'`);
                        }
                
                        this.values.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'values' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'values'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // values
        if (this.isValuesDefined && this.values !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.values === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.values.length);
            
                for (const item of this.values) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // values
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.values = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.values.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): MultiStringQuestion {
        // Method Implementation: simple
        const schema = MultiStringQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MultiStringQuestionZodSchemaShape): MultiStringQuestion {
        // Method Implementation: parseZodSchema
        return new MultiStringQuestion({
            name: schema.name,
            source: schema.source,
            displayKey: schema.displayKey,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => i),
            valueSource: schema.valueSource,
            values: schema.values?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'multiString',
            ...super.toJSON(),
            values: this.isValuesDefined ? (this.values?.map(item => (item === null ? undefined : item))) : undefined,
        };
    }

    public clone(): MultiStringQuestion {
        // Method Implementation: clone
        return new MultiStringQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            values: this.isValuesDefined ? this.values?.map(i => i) : undefined
        });
    }
}

export class MultiNumericQuestion extends AQuestion implements IMultiNumericQuestion {
    public static readonly modelName: string = 'MultiNumericQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'values' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MultiNumericQuestion.modelName;
    }

    private _values: number[];

    public get values(): number[] {
        // Method Implementation: simple
        return this._values;
    }

    public set values(value: number[]) {
        // Method Implementation: simple
        this._values = value;
        this._isValuesDefined = true;
    }

    private _isValuesDefined: boolean;

    public get isValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isValuesDefined;
    }

    public constructor();
    public constructor(source: IMultiNumericQuestion);
    public constructor(source: Partial<Pick<MultiNumericQuestion, 'values' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>);
    public constructor(source?: IMultiNumericQuestion | Partial<Pick<MultiNumericQuestion, 'values' | 'name' | 'source' | 'displayKey' | 'displayName' | 'helpText' | 'enabled' | 'visible' | 'validationState' | 'validationMessages' | 'valueSource'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isMultiNumericQuestionModel(source)) {
                if (source.isValuesDefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
            else {
                if (source.values !== undefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._values = [];
            this._isValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...MultiNumericQuestion.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValuesDefined && this.values !== undefined)
            definedProperties.push('values');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'values':
                return this.values;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValuesDefined && this.values !== undefined) {
            if (this.values !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.values.length));
                for (let i = 0; i < this.values.length; i++) {
                    const item = this.values[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('double'));
                
                        // Implementation: axon/write/double
                        writer.writeDoubleValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'values':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.values = [];
                
                    let item: number = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: double
                        if (arrayItemHeader.type === 'double') {
                            item = reader.readDoubleValue();
                        }
                        else if (arrayItemHeader.type === 'single') {
                            item = reader.readFloatValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'values' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'values'`);
                        }
                
                        this.values.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'values' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'values'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // values
        if (this.isValuesDefined && this.values !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.values === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.values.length);
            
                for (const item of this.values) {
                    // Item Implementation: double
                    writer.writeDoubleValue(item);
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // values
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: number;
            
                    this.values = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: double
                        item = reader.readDoubleValue();
            
                        this.values.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): MultiNumericQuestion {
        // Method Implementation: simple
        const schema = MultiNumericQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MultiNumericQuestionZodSchemaShape): MultiNumericQuestion {
        // Method Implementation: parseZodSchema
        return new MultiNumericQuestion({
            name: schema.name,
            source: schema.source,
            displayKey: schema.displayKey,
            displayName: schema.displayName,
            helpText: schema.helpText,
            enabled: schema.enabled,
            visible: schema.visible,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return QuestionValidationStates.Valid;
                case 'SoftInvalid':
                    return QuestionValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return QuestionValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => i),
            valueSource: schema.valueSource,
            values: schema.values?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            questionType: 'multiNumeric',
            ...super.toJSON(),
            values: this.isValuesDefined ? (this.values?.map(item => item)) : undefined,
        };
    }

    public clone(): MultiNumericQuestion {
        // Method Implementation: clone
        return new MultiNumericQuestion({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i) : undefined,
            valueSource: this.isValueSourceDefined ? this.valueSource : undefined,
            values: this.isValuesDefined ? this.values?.map(i => i) : undefined
        });
    }
}

export abstract class AAnswer implements IAnswer {
    public static readonly modelName: string = 'Answer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'displayKey', 'enabled', 'visible', 'assignmentState' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AAnswer.modelName;
    }

    private _name: string;

    private _displayKey: string;

    private _enabled: boolean;

    private _visible: boolean;

    private _assignmentState: Core.OptionStates;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get displayKey(): string {
        // Method Implementation: simple
        return this._displayKey;
    }

    public get enabled(): boolean {
        // Method Implementation: simple
        return this._enabled;
    }

    public get visible(): boolean {
        // Method Implementation: simple
        return this._visible;
    }

    public get assignmentState(): Core.OptionStates {
        // Method Implementation: simple
        return this._assignmentState;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set displayKey(value: string) {
        // Method Implementation: simple
        this._displayKey = value;
        this._isDisplayKeyDefined = true;
    }

    public set enabled(value: boolean) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set visible(value: boolean) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set assignmentState(value: Core.OptionStates) {
        // Method Implementation: simple
        this._assignmentState = value;
        this._isAssignmentStateDefined = true;
    }

    private _isNameDefined: boolean;

    private _isDisplayKeyDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isAssignmentStateDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isDisplayKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayKeyDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isAssignmentStateDefined(): boolean {
        // Method Implementation: simple
        return this._isAssignmentStateDefined;
    }

    public constructor();
    public constructor(source: IAnswer);
    public constructor(source: Partial<Pick<AAnswer, 'name' | 'displayKey' | 'enabled' | 'visible' | 'assignmentState'>>);
    public constructor(source?: IAnswer | Partial<Pick<AAnswer, 'name' | 'displayKey' | 'enabled' | 'visible' | 'assignmentState'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isAnswerModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isDisplayKeyDefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.isAssignmentStateDefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = Core.OptionStates.Assignable;
                    this._isAssignmentStateDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.displayKey !== undefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.assignmentState !== undefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = Core.OptionStates.Assignable;
                    this._isAssignmentStateDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._displayKey = '';
            this._isDisplayKeyDefined = false;
        
            this._enabled = false;
            this._isEnabledDefined = false;
        
            this._visible = false;
            this._isVisibleDefined = false;
        
            this._assignmentState = Core.OptionStates.Assignable;
            this._isAssignmentStateDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AAnswer.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isDisplayKeyDefined && this.displayKey !== undefined)
            definedProperties.push('displayKey');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined)
            definedProperties.push('assignmentState');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'displayKey':
                return this.displayKey;
            case 'enabled':
                return this.enabled;
            case 'visible':
                return this.visible;
            case 'assignmentState':
                return this.assignmentState;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            if (this.displayKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            if (this.assignmentState !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.assignmentState);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'displayKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayKey' cannot be null`);
                    this.displayKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayKey'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.enabled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'enabled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.visible = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'visible' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'assignmentState':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.assignmentState = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'assignmentState' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'assignmentState'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayKey
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.enabled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.visible);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // assignmentState
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.assignmentState);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayKey = <any>null;
                    break;
                case 1:
                    this.displayKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.enabled = reader.readBooleanValue();
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.visible = reader.readBooleanValue();
        }
        
        // assignmentState
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.assignmentState = reader.readEnumValue();
        }
    }

    public static parse(hash: unknown): AAnswer {
        // Method Implementation: simple
        const schema = AnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: AnswerZodSchemaShape): AAnswer {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.answerType)
        {
            case 'stringAnswer':
            case 'string':
                return StringAnswer.parse(schema);
            case 'numericAnswer':
            case 'numeric':
                return NumericAnswer.parse(schema);
            default:
                throw new Error(`answerType of ${ schema.answerType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            displayKey: this.isDisplayKeyDefined ? ((this.displayKey === null ? undefined : this.displayKey)) : undefined,
            enabled: this.isEnabledDefined ? (this.enabled) : undefined,
            visible: this.isVisibleDefined ? (this.visible) : undefined,
            assignmentState: this.isAssignmentStateDefined ? (Core.OptionStates[this.assignmentState]) : undefined,
        };
    }

    public abstract clone(): AAnswer;
}

export class StringAnswer extends AAnswer implements IStringAnswer {
    public static readonly modelName: string = 'StringAnswer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringAnswer.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringAnswer);
    public constructor(source: Partial<Pick<StringAnswer, 'value' | 'name' | 'displayKey' | 'enabled' | 'visible' | 'assignmentState'>>);
    public constructor(source?: IStringAnswer | Partial<Pick<StringAnswer, 'value' | 'name' | 'displayKey' | 'enabled' | 'visible' | 'assignmentState'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringAnswerModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringAnswer.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringAnswer {
        // Method Implementation: simple
        const schema = StringAnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringAnswerZodSchemaShape): StringAnswer {
        // Method Implementation: parseZodSchema
        return new StringAnswer({
            name: schema.name,
            displayKey: schema.displayKey,
            enabled: schema.enabled,
            visible: schema.visible,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Assignable':
                    return Core.OptionStates.Assignable;
                case 'Conflicting':
                    return Core.OptionStates.Conflicting;
                case 'Blocked':
                    return Core.OptionStates.Blocked;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            answerType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringAnswer {
        // Method Implementation: clone
        return new StringAnswer({
            name: this.isNameDefined ? this.name : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericAnswer extends AAnswer implements INumericAnswer {
    public static readonly modelName: string = 'NumericAnswer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericAnswer.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: INumericAnswer);
    public constructor(source: Partial<Pick<NumericAnswer, 'value' | 'name' | 'displayKey' | 'enabled' | 'visible' | 'assignmentState'>>);
    public constructor(source?: INumericAnswer | Partial<Pick<NumericAnswer, 'value' | 'name' | 'displayKey' | 'enabled' | 'visible' | 'assignmentState'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericAnswerModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericAnswer.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericAnswer {
        // Method Implementation: simple
        const schema = NumericAnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericAnswerZodSchemaShape): NumericAnswer {
        // Method Implementation: parseZodSchema
        return new NumericAnswer({
            name: schema.name,
            displayKey: schema.displayKey,
            enabled: schema.enabled,
            visible: schema.visible,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Assignable':
                    return Core.OptionStates.Assignable;
                case 'Conflicting':
                    return Core.OptionStates.Conflicting;
                case 'Blocked':
                    return Core.OptionStates.Blocked;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            answerType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): NumericAnswer {
        // Method Implementation: clone
        return new NumericAnswer({
            name: this.isNameDefined ? this.name : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export interface IProviderService {
    createEntity(entity: Core.Entity, layout: ModelLayout.Layout): Promise<Entity>;
}

export interface ILifecycleService {
    sanitize(entity: Entity, coreEntity: Core.Entity): Promise<void>;

    update(entity: Entity, coreEntity: Core.Entity, updates: string[]): Promise<void>;
}

export class ProviderServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public createEntity(entity: Core.Entity, layout?: ModelLayout.Layout): Promise<Entity>;
    public createEntity(actionContext: Wellspring.ActionContext, entity: Core.Entity, layout?: ModelLayout.Layout): Promise<Entity>;
    public async createEntity(...args: unknown[]): Promise<Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.union([
            Zod.tuple([
                // {"template":"model","className":"Core.Entity"}
                Zod.instanceof(Core.Entity)
            ]), 
            Zod.tuple([
                // {"template":"model","className":"Core.Entity"}
                Zod.instanceof(Core.Entity), 
                // {"template":"model","className":"ModelLayout.Layout"}
                Zod.instanceof(ModelLayout.Layout).optional()
            ])
        ]).check(args)) {
            const [ entity, layout ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createEntity');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createEntity', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("02eeb20191257c5ca810645321619b86b55d94870b15297a4a2e29595461c0e0");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (layout && layout !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (layout === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            layout.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createEntity`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                layout: JSON.parse(JSON.stringify(layout))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createEntity" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        layout: JSON.parse(JSON.stringify(layout))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        layout: JSON.parse(JSON.stringify(layout))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class LifecycleServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public sanitize(entity: Entity, coreEntity: Core.Entity): Promise<void>;
    public sanitize(actionContext: Wellspring.ActionContext, entity: Entity, coreEntity: Core.Entity): Promise<void>;
    public async sanitize(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity), 
            // {"template":"model","className":"Core.Entity"}
            Zod.instanceof(Core.Entity)
        ]).check(args)) {
            const [ entity, coreEntity ] = args;
        
            const activity = new Wellspring.DistributedActivity('lifecycle.sanitize');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('sanitize', 'utf8'));
                requestMetadata.add('service', Buffer.from('lifecycle', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("91dbc03b7ab4306edd38c0284edb95ef00806e5481663482cd986951c737ac46");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (coreEntity && coreEntity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (coreEntity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            coreEntity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].sanitize`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                coreEntity: JSON.parse(JSON.stringify(coreEntity))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "lifecycle/sanitize" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            entity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            coreEntity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `lifecycle[${this.identifier}].sanitize`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        coreEntity: JSON.parse(JSON.stringify(coreEntity))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].sanitize`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        coreEntity: JSON.parse(JSON.stringify(coreEntity))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public update(entity: Entity, coreEntity: Core.Entity, updates: string[]): Promise<void>;
    public update(actionContext: Wellspring.ActionContext, entity: Entity, coreEntity: Core.Entity, updates: string[]): Promise<void>;
    public async update(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity), 
            // {"template":"model","className":"Core.Entity"}
            Zod.instanceof(Core.Entity), 
            // {"template":"array","itemImplementation":{"template":"string"}}
            Zod.array(Zod.string())
        ]).check(args)) {
            const [ entity, coreEntity, updates ] = args;
        
            const activity = new Wellspring.DistributedActivity('lifecycle.update');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('update', 'utf8'));
                requestMetadata.add('service', Buffer.from('lifecycle', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("6557228169c3830985bebe73bc310d1756e94cd866b23e07b78bc8e129278ce3");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (coreEntity && coreEntity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (coreEntity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            coreEntity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (updates && updates !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (updates === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(updates.length);
                        
                            for (const item of updates) {
                                // Item Implementation: string
                                if (item === null)
                                {
                                    writer.writeByteValue(0);
                                }
                                else
                                {
                                    writer.writeByteValue(1);
                                    writer.writeStringValue(item);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].update`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                coreEntity: JSON.parse(JSON.stringify(coreEntity)),
                                updates: JSON.parse(JSON.stringify(updates))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "lifecycle/update" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            entity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            coreEntity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `lifecycle[${this.identifier}].update`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        coreEntity: JSON.parse(JSON.stringify(coreEntity)),
                        updates: JSON.parse(JSON.stringify(updates))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].update`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        coreEntity: JSON.parse(JSON.stringify(coreEntity)),
                        updates: JSON.parse(JSON.stringify(updates))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Entity':
                return new Entity();
            case 'Child':
                return new Child();
            case 'Group':
                return new Group();
            case 'StringQuestion':
                return new StringQuestion();
            case 'NumericQuestion':
                return new NumericQuestion();
            case 'BooleanQuestion':
                return new BooleanQuestion();
            case 'StringEnumerableQuestion':
                return new StringEnumerableQuestion();
            case 'NumericEnumerableQuestion':
                return new NumericEnumerableQuestion();
            case 'MultiStringQuestion':
                return new MultiStringQuestion();
            case 'MultiNumericQuestion':
                return new MultiNumericQuestion();
            case 'StringAnswer':
                return new StringAnswer();
            case 'NumericAnswer':
                return new NumericAnswer();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Entity':
                return new Entity(source);
            case 'Child':
                return new Child(source);
            case 'Group':
                return new Group(source);
            case 'StringQuestion':
                return new StringQuestion(source);
            case 'NumericQuestion':
                return new NumericQuestion(source);
            case 'BooleanQuestion':
                return new BooleanQuestion(source);
            case 'StringEnumerableQuestion':
                return new StringEnumerableQuestion(source);
            case 'NumericEnumerableQuestion':
                return new NumericEnumerableQuestion(source);
            case 'MultiStringQuestion':
                return new MultiStringQuestion(source);
            case 'MultiNumericQuestion':
                return new MultiNumericQuestion(source);
            case 'StringAnswer':
                return new StringAnswer(source);
            case 'NumericAnswer':
                return new NumericAnswer(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Entity':
                return Entity.parse(source);
            case 'Child':
                return Child.parse(source);
            case 'Group':
                return Group.parse(source);
            case 'StringQuestion':
                return StringQuestion.parse(source);
            case 'NumericQuestion':
                return NumericQuestion.parse(source);
            case 'BooleanQuestion':
                return BooleanQuestion.parse(source);
            case 'StringEnumerableQuestion':
                return StringEnumerableQuestion.parse(source);
            case 'NumericEnumerableQuestion':
                return NumericEnumerableQuestion.parse(source);
            case 'MultiStringQuestion':
                return MultiStringQuestion.parse(source);
            case 'MultiNumericQuestion':
                return MultiNumericQuestion.parse(source);
            case 'StringAnswer':
                return StringAnswer.parse(source);
            case 'NumericAnswer':
                return NumericAnswer.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
